<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="$t('resources.alternative_contents.title')">
    <template #actions>
      <v-btn v-if="totalItems || noData || loading" color="primary" class="left-icon" to="/alternative-contents/create">
        <plus-icon class="mr-2"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
    </template>

    <template #default>
      <delete-resource-modal :resource-name="resourceName"></delete-resource-modal>
      <va-list
        ref="list"
        :filter.sync="customFiltersValues"
        :filters="filters"
        disable-export
        disable-create
        @update:selected="getBulkSelectedItems"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <empty-list
            max-width="350px"
            :list-page="listPage"
            :title="$t('resources.alternative_contents.list_page_empty_list_title')"
            :light-image="emptyLightImg"
            :dark-image="emptyDarkImg"
            :route="{ name: 'alternative_contents_create' }">
            <template #subtitle>
              {{ $t("resources.alternative_contents.list_page_empty_list_subtitle") }}
            </template>
          </empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <v-btn
            @click="editResource('alternative_contents', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-pencil-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
          </v-btn>
        </template>
        <template #[`filter.q`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table :disable-clone="true" :fields="fields" class="va-table--selectable" @row-click="handleRowClick">
          <template #[`field.name`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>{{ item.name }}</span>
            </div>
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`field.createdAt`]="{ item }">
            {{ changeDateFormat(item.createdAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'Edit',
                  icon: 'mdi-pencil-outline',
                  function: editResource.bind(this, 'alternative_contents', item.id),
                },
                { title: 'Delete', icon: 'mdi-trash-can-outline', function: deleteItem.bind(this, item) },
              ]"></DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import PlusIcon from "@/components/icons/PlusIcon";
  import DataListActions from "@/components/custom/DataListActions";
  import EmptyList from "@/components/custom/EmptyList";
  import DeleteResourceModal from "@/components/resources/DeleteResourceModal";
  import RowClick from "@/mixins/rowClickMixin";
  // Images
  import emptyLightImg from "@/assets/images/empty-list/alternative-contents-empty-list-light.svg";
  import emptyDarkImg from "@/assets/images/empty-list/alternative-contents-empty-list-dark.svg";
  import debounce from "lodash.debounce";
  import definedAbility from "@/ability";

  export default {
    components: {
      ResourceListWrapper,
      PlusIcon,
      DataListActions,
      EmptyList,
      DeleteResourceModal,
    },
    mixins: [RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      title: {
        type: String,
        required: true,
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        ability: definedAbility(),
        emptyLightImg,
        emptyDarkImg,
        changeDateFormat,
        isUserUS,
        noData: false,
        loading: true,
        totalItems: 0,
        customFiltersValues: {},
        filters: [
          {
            source: "q",
            alwaysOn: true,
          },
        ],
        fields: [
          {
            source: "name",
            sortable: true,
            width: "34%",
          },
          {
            source: "updatedAt",
            sortable: true,
            width: "33%",
          },
          {
            source: "createdAt",
            sortable: true,
            width: "33%",
          },
        ],
        bulkSelectedItems: [],
      };
    },
    methods: {
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      getBulkSelectedItems(val) {
        this.bulkSelectedItems = val;
      },
      async deleteItem(item) {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: true,
          items: [item],
        });
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId } });
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
    },
  };
</script>

<style lang="scss" scoped>
  .logo-img {
    max-width: 36px;
    min-height: 36px;
    margin: 5px 0;
    border-radius: 50%;
  }
</style>
